import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useState } from "react";

import {
  PICKER_PAGE_SIZE,
  useDefinitionCases,
  useQuestions,
  type TestDefinition,
  type TestQuestion,
} from "@/api/tests-api";
import { formatCountdown } from "@/hooks/useTestCountdown";
import { RoomViewer } from "@/components/viewer/RoomViewer";

/**
 * See the exam the way a candidate will, without being one.
 *
 * Built from the REAL candidate component — the reading room's
 * `RoomViewer`, as both rounds read a case since 2026-10-07 — rather
 * than a mock-up of it. A preview that is a separate implementation
 * drifts from the thing it claims to show, and then it is worse than
 * nothing, because someone signs off an exam against a screen no
 * candidate will ever see.
 *
 * STEP ONE IS THE EXCEPTION, and deliberately so. It is now the fixed
 * application form, which is identical for every exam and reads and
 * writes the SIGNED-IN PERSON'S own row — an admin opening it would get
 * a 403, and rightly. So this step describes the form instead of
 * rendering it. Nothing is lost: there is no longer anything
 * exam-specific there to preview.
 *
 * What is deliberately absent is everything that only makes sense for a
 * real sitting: no clock, no attempt row, no recording, no integrity
 * signals, nothing written anywhere. The durations are shown as text so
 * the exam's shape is still legible.
 *
 * It reads the exam AS IT STANDS NOW. A candidate already assigned holds
 * a sealed copy which may differ — that is what the version labels in
 * Results are for.
 */

/** The application's sections, mirroring CandidateApplicationForm. */
const APPLICATION_SECTIONS: [string, string][] = [
  ["01", "About you"],
  ["02", "Registration & eligibility"],
  ["03", "Qualifications & experience"],
  ["04", "Systems & dictation"],
  ["05", "Reporting capacity by modality"],
  ["06", "Procedures"],
  ["07", "Your plan"],
  ["08", "Situational judgement"],
  ["09", "Return-of-service commitment"],
];

type Step = "brief" | "intro" | "cases";

export function CandidatePreviewModal({
  exam,
  onClose,
}: {
  exam: TestDefinition | null;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>("brief");
  const [caseIndex, setCaseIndex] = useState(0);

  const cases = useDefinitionCases(exam?.id ?? null);
  // THE PREVIEW HAS TO KNOW WHICH ROUND IT IS PREVIEWING. The two
  // sittings share no journey and no viewer, and a preview that shows
  // one candidate's screens under the other's name is worse than no
  // preview -- it invites sign-off on something nobody will ever see.
  const isVideo = exam?.kind === "VIDEO_ROUND";
  // Only for a video round: the questions ARE the exam there, so the
  // preview has to name them. A reporting exam's step one is the fixed
  // application and needs no fetch.
  const pool = useQuestions(false, 1, PICKER_PAGE_SIZE, "VIDEO_ROUND");
  const asked = (exam?.selected_question_ids ?? [])
    .map((id) => (pool.data?.items ?? []).find((q) => q.id === id))
    .filter((q): q is TestQuestion => !!q);

  const liveCases = (cases.data ?? []).filter((c) => c.phase === "LIVE");
  const practiceCases = (cases.data ?? []).filter((c) => c.phase === "DRY_RUN");

  if (!exam) return null;

  const current = liveCases[caseIndex];

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) {
          setStep("brief");
          setCaseIndex(0);
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          data-cy="candidate-preview-modal"
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[94vh] w-[96vw] max-w-[1600px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-border bg-background-card shadow-theme-lg"
        >
          {/* NEARLY THE WHOLE WINDOW, because the candidate's sitting IS
              the whole window. At max-w-5xl the case rail and the brief
              column left the images 319px wide against roughly 900 in a
              real sitting -- the same layout at a quarter of the size,
              which answers "what will they see" with a picture nobody
              will see. */}
          <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-5 pb-4">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                {exam.title}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                Preview — no clock, nothing recorded, nothing saved. This is
                the exam as it stands now; candidates already assigned keep
                the version they were given.
              </Dialog.Description>
            </div>
            <Dialog.Close
              data-cy="btn-close-candidate-preview"
              aria-label="Close"
              className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <div className="flex gap-1 border-b border-border px-6">
            {(
              isVideo
                ? ([
                    ["brief", "1 · Brief"],
                    ["intro", "2 · Questions to camera"],
                    ["cases", "3 · Cases to dictate"],
                  ] as [Step, string][])
                : ([
                    ["brief", "1 · Brief"],
                    ["intro", "2 · Questions"],
                    ["cases", "3 · Cases"],
                  ] as [Step, string][])
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-cy={`preview-step-${key}`}
                onClick={() => setStep(key)}
                className={[
                  "-mb-px border-b-2 px-3 py-2 text-xs transition-colors",
                  step === key
                    ? "border-accent font-semibold text-accent"
                    : "border-transparent text-foreground-muted hover:text-foreground",
                ].join(" ")}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {step === "brief" && isVideo && (
              <div data-cy="preview-brief-video" className="max-w-2xl">
                {/* THE RULES THIS ROUND ACTUALLY RUNS BY, not the
                    reporting exam's. Almost every line below is the
                    OPPOSITE of the one beside it: per-question clocks
                    rather than one, a fixed order rather than any
                    order, no practice, no retake. */}
                <p className="text-sm text-foreground">
                  A screening round of{" "}
                  <strong>{asked.length}</strong> question
                  {asked.length === 1 ? "" : "s"} answered to camera
                  {liveCases.length > 0 && (
                    <>
                      , then {liveCases.length} case
                      {liveCases.length === 1 ? "" : "s"} to dictate
                    </>
                  )}
                  .
                </p>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-foreground-muted">
                  <li>
                    Each item has its OWN clock. {formatCountdown(exam.duration_seconds)}{" "}
                    is only the outer bound on the whole sitting.
                  </li>
                  <li>
                    One continuous recording, one attempt, no retake and no
                    playback.
                  </li>
                  <li>
                    They cannot go back to an earlier question — the order is
                    fixed.
                  </li>
                  <li>
                    There is no application step and no practice run in this
                    round.
                  </li>
                  <li>It submits itself when the last answer ends.</li>
                </ul>
                {asked.length === 0 && liveCases.length === 0 && (
                  <p className="mt-4 text-sm text-status-urgent">
                    This round has neither questions nor cases — a candidate
                    cannot sit it.
                  </p>
                )}
              </div>
            )}

            {step === "brief" && !isVideo && (
              <div data-cy="preview-brief" className="max-w-2xl">
                <p className="text-sm text-foreground">
                  This assessment lasts{" "}
                  <strong>{formatCountdown(exam.duration_seconds)}</strong>{" "}
                  across {liveCases.length} case
                  {liveCases.length === 1 ? "" : "s"}.
                </p>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-foreground-muted">
                  <li>The clock starts when the candidate presses Start.</li>
                  <li>Once started it does not pause.</li>
                  <li>They can work through the cases in any order.</li>
                  <li>Leaving the assessment ends it.</li>
                </ul>
                {exam.dry_run_duration_seconds === null ? (
                  <p className="mt-4 text-sm text-foreground-muted">
                    No practice run is offered.
                  </p>
                ) : (
                  <p className="mt-4 text-sm text-foreground-muted">
                    A practice run of{" "}
                    <strong>
                      {formatCountdown(exam.dry_run_duration_seconds)}
                    </strong>{" "}
                    on {practiceCases.length} separate case
                    {practiceCases.length === 1 ? "" : "s"} is offered first,
                    and does not count.
                  </p>
                )}
                {liveCases.length === 0 && (
                  <p className="mt-4 text-sm text-status-urgent">
                    This exam has no assessment cases — a candidate cannot sit
                    it.
                  </p>
                )}
              </div>
            )}

            {step === "intro" && isVideo && (
              <div data-cy="preview-intro-video" className="max-w-2xl">
                <p className="sk-hint">
                  A screening round has NO application step. The candidate
                  checks their camera, presses Start, and is then asked these
                  questions one at a time — each against its own clock, spoken
                  to camera, with no way back to an earlier one. The round
                  submits itself when the last answer ends.
                </p>
                {asked.length === 0 ? (
                  <p className="mt-4 text-sm text-foreground-muted">
                    No questions selected yet, so this round would open on its
                    cases.
                  </p>
                ) : (
                  <ol className="mt-4 space-y-2">
                    {asked.map((q, i) => (
                      <li key={q.id} className="flex items-baseline gap-3">
                        <span className="sk-code">{String(i + 1).padStart(2, "0")}</span>
                        <span className="min-w-0 flex-1 text-sm">{q.prompt}</span>
                        <span className="shrink-0 text-xs tabular-nums text-foreground-muted">
                          {formatCountdown(
                            Number(q.config_json?.allotted_seconds ?? 0),
                          )}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
                <button
                  type="button"
                  className="sk-btn mt-5"
                  onClick={() => setStep("cases")}
                >
                  On to the cases
                </button>
              </div>
            )}

            {step === "intro" && !isVideo && (
              <div data-cy="preview-intro" className="max-w-2xl">
                <p className="sk-hint">
                  Every candidate answers the same nine-section application
                  before the clock starts. It is fixed for all exams and is
                  not configured here.
                </p>
                <ol className="mt-4 space-y-2">
                  {APPLICATION_SECTIONS.map(([code, title]) => (
                    <li key={code} className="flex items-baseline gap-3">
                      <span className="sk-code">{code}</span>
                      <span className="text-sm">{title}</span>
                    </li>
                  ))}
                </ol>
                <p className="sk-help mt-4">
                  Section 04 and 05 are what the panel console&rsquo;s stage 07
                  transfers from.
                </p>
                <button
                  type="button"
                  className="sk-btn mt-5"
                  onClick={() => setStep("cases")}
                >
                  On to the cases
                </button>
              </div>
            )}

            {step === "cases" && (
              <div data-cy="preview-cases">
                {liveCases.length === 0 ? (
                  <p className="text-sm text-foreground-muted">
                    No assessment cases yet.
                  </p>
                ) : (
                  <>
                    {/* THE CANDIDATE'S OWN LAYOUT, not an approximation
                        of it. This step existed to answer "what will they
                        see" and answered it wrongly: the cases were tabs
                        across the top, the brief sat above the images
                        full-width, and there was no case counter -- while
                        the real sitting puts the case list in a LEFT RAIL
                        and the brief in a sticky right column beside the
                        images. An admin checking an exam before assigning
                        it was being shown a different screen from the one
                        the candidate gets.

                        Only TestCaseViewer was ever shared. The frame
                        around it is now the same shape too: rail, then a
                        [1fr, 22rem] grid, stacking below xl exactly as
                        the sitting does. */}
                    <div className="flex min-h-0 gap-6">
                      <nav
                        data-cy="preview-case-nav"
                        className="flex w-60 shrink-0 flex-col self-start overflow-auto rounded-lg border border-border bg-background"
                      >
                        <div className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                          Cases (0/{liveCases.length})
                        </div>
                        <ul>
                          {liveCases.map((c, i) => (
                            <li key={c.id}>
                              <button
                                type="button"
                                data-cy="preview-case-tab"
                                onClick={() => setCaseIndex(i)}
                                className={[
                                  "flex w-full items-center gap-2 border-l-2 px-4 py-2 text-left text-sm transition-colors",
                                  i === caseIndex
                                    ? "border-accent bg-table-row-hover text-foreground"
                                    : "border-transparent text-foreground-muted hover:bg-table-row-hover",
                                ].join(" ")}
                              >
                                <span className="w-6 shrink-0 text-xs tabular-nums text-foreground-muted">
                                  {i + 1}
                                </span>
                                <span className="min-w-0 flex-1 truncate">
                                  {c.title ?? "Untitled"}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </nav>

                      <div className="min-w-0 flex-1">
                    {current && (
                      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <h2 className="text-lg font-semibold text-foreground">
                              {current.title ?? "Untitled"}
                            </h2>
                            <span
                              data-cy="preview-case-position"
                              className="shrink-0 rounded-full bg-background-card px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted"
                            >
                              Case {caseIndex + 1} of {liveCases.length}
                            </span>
                          </div>
                          <p className="mt-1 text-xs uppercase tracking-wide text-foreground-muted">
                            {[current.modality, current.body_part]
                              .filter(Boolean)
                              .join(" · ") || "Case"}
                          </p>

                          <div className="mt-4 flex h-[68vh] min-h-[22rem] flex-col overflow-hidden rounded-lg">
                            {/* THE SAME VIEWER THE CANDIDATE GETS, in
                                either round: the reading room, embedded.
                                WITH the staff note when a case falls back
                                to its rendered images -- the candidate
                                never sees it, and without it an admin
                                previewing a JPEG-only case reads "the new
                                viewer is not on this round" (reported
                                2026-10-07, about exactly that). No full
                                screen inside this Radix modal: a full-
                                screen layer in the body cannot take
                                clicks under it. */}
                            <RoomViewer
                              source={{
                                kind: "library",
                                caseId: current.test_case_id,
                              }}
                              testCase={{
                                title: current.title ?? "Case",
                                modality: current.modality ?? null,
                                body_part: current.body_part ?? null,
                                clinical_history: current.clinical_history ?? null,
                              }}
                              explainFallback
                              allowFullScreen={false}
                            />
                          </div>
                        </div>

                        <div className="min-w-0 xl:sticky xl:top-6 xl:self-start">
                          {current.clinical_history ? (
                            <p className="mb-4 whitespace-pre-line rounded-lg border border-border bg-background-card p-4 text-sm text-foreground">
                              {current.clinical_history}
                            </p>
                          ) : (
                            <p className="mb-4 rounded-lg border border-border bg-background-card p-4 text-sm text-status-urgent">
                              No clinical history — the candidate gets no
                              context for this case.
                            </p>
                          )}
                          <p className="rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground-muted">
                            The candidate records their report here, beside the
                            images. Recording is disabled in preview.
                          </p>
                        </div>
                      </div>
                    )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
